import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Popconfirm, Tag, Input } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, FileTextOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { ProForm, ProFormText, ProFormSelect, ProFormDigit, ProFormDateTimePicker, ModalForm, ProFormTextArea } from '@ant-design/pro-components';

interface Bid {
  id: number;
  title: string;
  project_type: number;
  city_name: string;
  company_name: string;
  purchasie_type: number;
  price: number;
  charge_type: number;
  sale_start_time: string;
  sale_end_time: string;
  check_status: number;
  recommend: number;
  status_flag: number;
  create_time: string;
}

export default function Bids() {
  const [bids, setBids] = useState<Bid[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingBid, setEditingBid] = useState<Bid | null>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [form] = ProForm.useForm();

  useEffect(() => {
    fetchBids();
  }, []);

  const fetchBids = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch('/api/admin/bids', {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setBids(resp.data || []);
      } else {
        message.error(resp?.error || '获取标书列表失败');
      }
    } catch (err) {
      message.error('获取标书列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingBid(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: Bid) => {
    setEditingBid(record);
    form.setFieldsValue({
      ...record,
      sale_start_time: record.sale_start_time ? new Date(record.sale_start_time) : undefined,
      sale_end_time: record.sale_end_time ? new Date(record.sale_end_time) : undefined,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/bids/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success('删除成功');
        fetchBids();
      } else {
        message.error(resp?.error || '删除失败');
      }
    } catch (err) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const token = localStorage.getItem('admin_token');
      const url = editingBid ? `/api/admin/bids/${editingBid.id}` : '/api/admin/bids';
      const method = editingBid ? 'PUT' : 'POST';

      const resp = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(values),
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success(editingBid ? '更新成功' : '创建成功');
        setModalVisible(false);
        fetchBids();
      } else {
        message.error(resp?.error || '操作失败');
      }
    } catch (err) {
      message.error('操作失败');
    }
  };

  // 过滤数据
  const filteredBids = bids.filter(b => 
    b.title?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    b.company_name?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    b.city_name?.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  const columns: ColumnsType<Bid> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      render: (id) => <span style={{ color: '#6b7280', fontFamily: 'monospace' }}>#{id}</span>
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (text) => <span style={{ fontWeight: 500, color: '#1f2937' }}>{text}</span>
    },
    {
      title: '项目性质',
      dataIndex: 'project_type',
      key: 'project_type',
      render: (type: number) => {
        const types: Record<number, { text: string; color: string }> = {
          1: { text: '工程', color: 'blue' },
          2: { text: '货物', color: 'green' },
          3: { text: '服务', color: 'purple' },
        };
        const t = types[type] || { text: '未知', color: 'default' };
        return <Tag color={t.color} style={{ borderRadius: 12 }}>{t.text}</Tag>;
      },
    },
    {
      title: '城市',
      dataIndex: 'city_name',
      key: 'city_name',
    },
    {
      title: '企业名称',
      dataIndex: 'company_name',
      key: 'company_name',
      ellipsis: true,
    },
    {
      title: '价格',
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => (
        <span style={{ color: '#f59e0b', fontWeight: 600 }}>
          ¥{price?.toFixed(2) || '0.00'}
        </span>
      ),
    },
    {
      title: '审核状态',
      dataIndex: 'check_status',
      key: 'check_status',
      render: (status: number) => {
        const statusMap: Record<number, { text: string; color: string }> = {
          0: { text: '未审核', color: 'warning' },
          1: { text: '已审核', color: 'success' },
          2: { text: '审核不通过', color: 'error' },
        };
        const s = statusMap[status] || statusMap[0];
        return <Tag color={s.color} style={{ borderRadius: 12 }}>{s.text}</Tag>;
      },
    },
    {
      title: '推荐',
      dataIndex: 'recommend',
      key: 'recommend',
      render: (recommend: number) => (
        recommend === 1 ? <Tag color="red" style={{ borderRadius: 12 }}>推荐</Tag> : '-'
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      key: 'create_time',
      render: (text) => <span style={{ color: '#6b7280', fontSize: 13 }}>{text}</span>
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ color: '#3b82f6' }}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个标书吗？"
            description="删除后将无法恢复"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="admin-page">
      {/* 页面头部 */}
      <div className="admin-page-header">
        <div className="admin-page-title">
          <div className="admin-page-title-icon">
            <FileTextOutlined />
          </div>
          <div>
            <h2>标书管理</h2>
            <p>管理所有招标项目标书</p>
          </div>
        </div>
        
        <Space size="middle">
          <Input
            placeholder="搜索标书..."
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            style={{ width: 220, borderRadius: 10 }}
            allowClear
          />
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchBids}
            style={{ borderRadius: 10 }}
          >
            刷新
          </Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAdd}
            style={{ 
              borderRadius: 10,
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              border: 'none',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
            }}
          >
            新增标书
          </Button>
        </Space>
      </div>
      
      {/* 表格内容 */}
      <div className="admin-page-content">
        <div style={{ marginBottom: 16, color: '#6b7280', fontSize: 14 }}>
          共 <span style={{ color: '#3b82f6', fontWeight: 600 }}>{filteredBids.length}</span> 条记录
        </div>
        
        <Table
          columns={columns}
          dataSource={filteredBids}
          loading={loading}
          rowKey="id"
          className="admin-table"
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
        />
      </div>
      
      {/* 编辑弹窗 */}
      <ModalForm
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FileTextOutlined style={{ color: 'white', fontSize: 16 }} />
            </div>
            <span>{editingBid ? '编辑标书' : '新增标书'}</span>
          </div>
        }
        open={modalVisible}
        onOpenChange={setModalVisible}
        form={form}
        onFinish={handleSubmit}
        width={800}
        modalProps={{
          destroyOnHidden: true,
          centered: true,
        }}
      >
        <div style={{ marginTop: 24 }}>
          <ProFormText
            name="title"
            label="标题"
            placeholder="请输入标书标题"
            rules={[{ required: true, message: '请输入标题' }]}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <ProFormSelect
              name="project_type"
              label="项目性质"
              placeholder="请选择项目性质"
              options={[
                { label: '工程', value: 1 },
                { label: '货物', value: 2 },
                { label: '服务', value: 3 },
              ]}
              rules={[{ required: true, message: '请选择项目性质' }]}
            />
            <ProFormSelect
              name="purchasie_type"
              label="采购方式"
              placeholder="请选择采购方式"
              options={[
                { label: '公开招标', value: 1 },
                { label: '邀请招标', value: 2 },
                { label: '竞争性谈判', value: 3 },
                { label: '询价', value: 4 },
              ]}
              rules={[{ required: true, message: '请选择采购方式' }]}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <ProFormText
              name="city_name"
              label="城市名称"
              placeholder="请输入城市名称"
            />
            <ProFormText
              name="company_name"
              label="企业名称"
              placeholder="请输入企业名称"
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <ProFormDigit
              name="price"
              label="信息服务费"
              placeholder="请输入价格"
              min={0}
              fieldProps={{ precision: 2 }}
            />
            <ProFormSelect
              name="charge_type"
              label="计费方式"
              placeholder="请选择计费方式"
              options={[
                { label: '按项目收费', value: 1 },
                { label: '按包收费', value: 2 },
              ]}
              rules={[{ required: true, message: '请选择计费方式' }]}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <ProFormDateTimePicker
              name="sale_start_time"
              label="售卖开始时间"
              placeholder="请选择开始时间"
            />
            <ProFormDateTimePicker
              name="sale_end_time"
              label="售卖结束时间"
              placeholder="请选择结束时间"
            />
          </div>
          <ProFormTextArea
            name="tips"
            label="备注"
            placeholder="请输入备注信息"
            fieldProps={{ rows: 3 }}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <ProFormSelect
              name="check_status"
              label="审核状态"
              placeholder="请选择审核状态"
              options={[
                { label: '未审核', value: 0 },
                { label: '已审核', value: 1 },
                { label: '审核不通过', value: 2 },
              ]}
            />
            <ProFormSelect
              name="recommend"
              label="推荐到首页"
              placeholder="请选择是否推荐"
              options={[
                { label: '不推荐', value: 0 },
                { label: '推荐', value: 1 },
              ]}
            />
          </div>
        </div>
      </ModalForm>
    </div>
  );
}
